import { Box, Button, FormControl, FormLabel, Select } from '@chakra-ui/react'
import type { InsuranceParamsPagedQuery } from 'BrokerTsLib/src/dtos'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { useContext } from 'react'

import { Card, CardBody } from '../../../components/Card'
import { Datepicker } from '../../../components/Datepicker'
import { InputGroup } from '../../../components/InputGroup'
import { Loading } from '../../../components/Loading'
import { useInsurers } from '../../../hooks'
import { useApp } from '../../../hooks/useApp'
import { useGetOrganizationUnits, useGetUsers } from '../../../hooks/useUser'
import { Search } from '../../../icons'
import { conceptsTravelContext } from '../../../pages/cestovko/concepts'
import { contractsTravelContext } from '../../../pages/cestovko/contracts'
import { SearchWrapper } from '../SearchWrapper'

interface SearchFormProps {
  onSubmit: (search: InsuranceParamsPagedQuery) => void,
  filter: InsuranceParamsPagedQuery,
  listType: 'contracted' | 'opened'
}

export const TravelSearchForm = ({ onSubmit, filter, listType }: SearchFormProps) => {
  const { setCurrentPage, loading: loadingList } = useContext(listType == 'opened' ? conceptsTravelContext : contractsTravelContext)
  const { app } = useApp()

  const methods = useForm<InsuranceParamsPagedQuery>({
    defaultValues: filter,
  })

  const { register, getValues, setValue, handleSubmit, formState: { isSubmitting } } = methods

  const { data: allInsurers, isFetched: isFetchedInsurers } = useInsurers(null, listType == 'contracted')
  const { data: organizationUnits, isFetched: isFetchedOrganizationUnits } = useGetOrganizationUnits()
  const { data: users, isFetched: isFetchedUsers } = useGetUsers()

  const loading = !isFetchedInsurers || !isFetchedUsers || !isFetchedOrganizationUnits

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {loading &&
          <Card>
            <CardBody>
              <Loading large={true} />
            </CardBody>
          </Card>
        }

        {!loading &&
          <SearchWrapper>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="offerNumber">Číslo nabídky</FormLabel>

              <InputGroup id="offerNumber" placeholder="Např: 73622" {...register('insuranceParamsId')} />
            </FormControl>

            {listType == 'contracted' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="contractNumber">Číslo smlouvy</FormLabel>

                  <InputGroup id="contractNumber" placeholder="Např: 73622" {...register('contractNumber')} />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateFrom">Datum sjednání od</FormLabel>

                  <Datepicker
                    date={getValues('dateContractedFrom')}
                    onDateChange={(date) => setValue('dateContractedFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateTo">Datum sjednání do</FormLabel>

                  <Datepicker
                    date={getValues('dateContractedTo')}
                    onDateChange={(date) => setValue('dateContractedTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {listType == 'opened' &&
              <>
                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateFrom">Datum od</FormLabel>

                  <Datepicker
                    date={getValues('dateCreatedFrom')}
                    onDateChange={(date) => setValue('dateCreatedFrom', formatDate(date))}
                  />
                </FormControl>

                <FormControl variant="fullWidth">
                  <FormLabel htmlFor="dateTo">Datum do</FormLabel>

                  <Datepicker
                    date={getValues('dateCreatedTo')}
                    onDateChange={(date) => setValue('dateCreatedTo', formatDate(date))}
                  />
                </FormControl>
              </>
            }

            {(allInsurers?.length ?? 0) > 0 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="insuranceCompany">Pojišťovna</FormLabel>

                <Select {...register('insurerId')}>
                  <option value=''>
                    Vyberte
                  </option>
                  {allInsurers?.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.description}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insureeName">Pojistník (příjmení/název firmy)</FormLabel>

              <InputGroup id="insureeName" placeholder="Např: Novák" {...register('legalPersonName')} />
            </FormControl>

            <FormControl variant="fullWidth">
              <FormLabel htmlFor="insureeNumber">Pojistník (rodné číslo/IČO)</FormLabel>

              <InputGroup
                id="insureeNumber"
                placeholder="Např: 9001012233"
                {...register('legalPersonIdentification')}
              />
            </FormControl>

            {(organizationUnits?.data.length ?? 0) > 1 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="organization">Organizační jednotka</FormLabel>

                <Select {...register('ouId')}>
                  <option value=''>
                    Vyberte organizaci
                  </option>
                  {organizationUnits?.data.map((company) => {
                    return (
                      <option key={company.id} value={company.id.toString()}>
                        {company.name}
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            {(users?.data.length ?? 0) > 1 &&
              <FormControl variant="fullWidth">
                <FormLabel htmlFor="user">Uživatel</FormLabel>

                <Select {...register('userId')}>
                  <option value=''>
                    Vyberte uživatele
                  </option>
                  {users?.data.map((user) => {
                    return (
                      <option key={user.userId} value={user.userId}>
                        {user.surname} {user.name} ({organizationUnits?.data.find(x => x.id.toString() === user.ouId?.toString())?.name})
                      </option>
                    )
                  })}
                </Select>
              </FormControl>
            }

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 4,
                rowGap: 4,
                alignItems: 'end',
                justifyContent: 'end',
                gridColumn: { base: '', md: '3 / 5' },
              }}
            >
              <Button type="submit" colorScheme="primary" onClick={() => setCurrentPage(1)} isLoading={isSubmitting || loadingList}>
                <Search />
                Vyhledat
              </Button>
            </Box>

          </SearchWrapper>
        }
      </form>
    </FormProvider>
  )
}
